import type { LscMyClaimOpportunity } from './types';

export const CLAIM_OPPORTUNITY_PANEL_TITLE = 'Claim your league profile';
export const CLAIM_OPPORTUNITY_PANEL_INTRO =
  'We found imported league profiles that may match your Discord account. Request a claim so commissioners can link your team, schedule, and VODs.';
export const CLAIM_OPPORTUNITY_LOADING_COPY = 'Checking for imported league profiles…';
export const CLAIM_OPPORTUNITY_EMPTY_COPY =
  'No claim opportunities right now. When your commissioner imports members from NeonSportz, matching profiles will appear here.';
export const CLAIM_OPPORTUNITY_ERROR_COPY = 'Could not load claim opportunities.';
export const CLAIM_OPPORTUNITY_DISCORD_COPY =
  'Connect Discord so Stream Center can match your imported league profile.';
export const CLAIM_OPPORTUNITY_REJECTION_HEADING = 'Commissioner note';

export type ClaimOpportunityBadgeStatus =
  | 'none'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'linked'
  | 'ambiguous';

export function claimOpportunityBadgeStatus(
  item: Pick<LscMyClaimOpportunity, 'claim_status'>
): ClaimOpportunityBadgeStatus {
  const status = item.claim_status || 'none';
  if (
    status === 'none' ||
    status === 'pending' ||
    status === 'approved' ||
    status === 'rejected' ||
    status === 'linked' ||
    status === 'ambiguous'
  ) {
    return status;
  }
  return 'none';
}

export function claimOpportunityBadgeLabel(status: ClaimOpportunityBadgeStatus): string {
  switch (status) {
    case 'none':
      return 'Available to claim';
    case 'pending':
      return 'Pending review';
    case 'approved':
    case 'linked':
      return 'Profile linked';
    case 'rejected':
      return 'Not approved';
    case 'ambiguous':
      return 'Needs commissioner review';
    default:
      return 'Available to claim';
  }
}

export function claimOpportunityBadgeClass(status: ClaimOpportunityBadgeStatus): string {
  switch (status) {
    case 'none':
      return 'bg-sky-500/20 text-sky-200 border-sky-500/40';
    case 'pending':
      return 'bg-amber-500/20 text-amber-200 border-amber-500/40';
    case 'approved':
    case 'linked':
      return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40';
    case 'rejected':
      return 'bg-rose-500/20 text-rose-200 border-rose-500/40';
    case 'ambiguous':
      return 'bg-secondary-600/40 text-secondary-300 border-secondary-500/40';
    default:
      return 'bg-secondary-600/40 text-secondary-300 border-secondary-500/40';
  }
}

export function claimOpportunityStatusMessage(item: LscMyClaimOpportunity): string {
  const status = claimOpportunityBadgeStatus(item);
  switch (status) {
    case 'none':
      return 'Request a claim so your commissioner can link this imported profile to your account.';
    case 'pending':
      return 'Your claim request is waiting for commissioner approval. You can open the league while you wait.';
    case 'approved':
    case 'linked':
      return 'Your league profile is linked. Open the league to view schedule, streams, and archive.';
    case 'rejected':
      return 'Your claim was not approved. Review the commissioner note below or ask them to verify your profile.';
    case 'ambiguous':
      return 'We found more than one possible profile. Ask your commissioner to verify which team is yours.';
    default:
      return 'Imported league profile available to claim.';
  }
}

export function formatClaimRejectionReason(reason?: string | null): string | null {
  const trimmed = (reason ?? '').trim();
  return trimmed || null;
}

export function claimOpportunityShowsViewStatus(item: LscMyClaimOpportunity): boolean {
  const status = claimOpportunityBadgeStatus(item);
  return status === 'pending' || status === 'rejected' || status === 'approved' || status === 'linked';
}

export function claimOpportunityViewStatusLabel(item: LscMyClaimOpportunity): string {
  const status = claimOpportunityBadgeStatus(item);
  if (status === 'pending') {
    return 'View claim status';
  }
  if (status === 'rejected') {
    return 'View claim details';
  }
  return 'View linked profile';
}

export function claimOpportunityShowsRequestButton(item: LscMyClaimOpportunity): boolean {
  return Boolean(
    item.can_request_claim &&
      item.claim_status === 'none' &&
      item.candidate_member_id &&
      item.league_id
  );
}

export function claimOpportunityShowsOpenLeague(item: LscMyClaimOpportunity): boolean {
  return Boolean(item.can_open_league && item.league_id);
}

export function formatClaimSourceProvider(provider?: string | null): string {
  if (!provider) return 'NeonSportz';
  if (provider.toLowerCase() === 'neonsportz') return 'NeonSportz';
  return provider;
}
